import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchLiveMotion, mediaStill, parseMediaValue } from "@/lib/livePhotoMedia";

export const LivePhotoSurface = ({ value, alt = "photo", className = "", imageClassName = "", testId = "live-photo", onActivate, eagerMotion = false, autoPlayOnce = false }) => {
  const media = parseMediaValue(value);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const objectUrlRef = useRef("");
  const playedRef = useRef(false);
  const startPointRef = useRef(null);
  const autoPlayedRef = useRef("");
  const [videoUrl, setVideoUrl] = useState("");
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const reducedMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  const loadMotion = useCallback(async () => {
    if (!media.live || videoUrl || loading || failed || !navigator.onLine) {
      if (!navigator.onLine) setFailed(true);
      return videoUrl;
    }
    setLoading(true);
    try {
      const url = await fetchLiveMotion(value);
      objectUrlRef.current = url;
      setVideoUrl(url);
      return url;
    } catch {
      setFailed(true);
      return "";
    } finally { setLoading(false); }
  }, [failed, loading, media.live, value, videoUrl]);

  useEffect(() => {
    setVideoUrl("");
    setPlaying(false);
    setFailed(false);
    autoPlayedRef.current = "";
    return () => {
      window.clearTimeout(timerRef.current);
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = "";
      }
    };
  }, [value]);

  useEffect(() => {
    if (eagerMotion && media.live && !videoUrl && !loading && !failed) loadMotion();
  }, [eagerMotion, failed, loadMotion, loading, media.live, videoUrl]);

  useEffect(() => {
    if (!autoPlayOnce || !media.live || !videoUrl || autoPlayedRef.current === value) return;
    autoPlayedRef.current = value;
    setPlaying(true);
  }, [autoPlayOnce, media.live, value, videoUrl]);

  useEffect(() => {
    if (!playing || !videoUrl || !videoRef.current) return;
    videoRef.current.currentTime = 0;
    videoRef.current.muted = false;
    videoRef.current.play().catch(() => {
      if (!videoRef.current) return;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => setFailed(true));
    });
  }, [playing, videoUrl]);

  const start = async () => {
    if (!media.live || failed) return;
    playedRef.current = true;
    setPlaying(true);
    await loadMotion();
  };
  const stop = () => {
    window.clearTimeout(timerRef.current);
    setPlaying(false);
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; }
  };
  const onTouchStart = (event) => {
    if (!media.live || reducedMotion) return;
    startPointRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    timerRef.current = window.setTimeout(start, 200);
  };
  const onTouchMove = (event) => {
    const point = startPointRef.current;
    if (!point) return;
    if (Math.abs(event.touches[0].clientX - point.x) > 10 || Math.abs(event.touches[0].clientY - point.y) > 10) {
      window.clearTimeout(timerRef.current);
      startPointRef.current = null;
    }
  };
  const activate = () => {
    if (playedRef.current) { playedRef.current = false; return; }
    if (media.live && reducedMotion) {
      if (playing) stop(); else start();
      return;
    }
    onActivate?.();
  };

  return (
    <div
      role={onActivate || media.live ? "button" : undefined}
      tabIndex={onActivate || media.live ? 0 : undefined}
      aria-label={media.live ? `${alt}. Live Photo — press and hold to play.` : alt}
      className={`relative overflow-hidden select-none touch-pan-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${!reducedMotion && playing ? "scale-[0.98]" : ""} transition-transform duration-200 ${className}`}
      onMouseDown={() => {
        if (!reducedMotion && media.live) timerRef.current = window.setTimeout(start, 180);
      }}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={stop}
      onTouchCancel={stop}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === " ") { event.preventDefault(); start(); }
        if (event.key === "Enter") activate();
      }}
      onKeyUp={(event) => { if (event.key === " ") stop(); }}
      data-testid={testId}
    >
      {media.live && videoUrl && <video ref={videoRef} src={videoUrl} playsInline onEnded={() => { setPlaying(false); if (videoRef.current) videoRef.current.currentTime = 0; }} className={`absolute inset-0 w-full h-full object-cover pointer-events-none ${imageClassName}`} data-testid={`${testId}-motion`} />}
      <img src={mediaStill(value)} alt={alt} draggable={false} className={`w-full h-full object-cover pointer-events-none transition-opacity duration-300 ${playing && videoUrl ? "opacity-0" : "opacity-100"} ${imageClassName}`} data-testid={`${testId}-still`} />
    </div>
  );
};